import { useContext } from 'react'

import {
  Icon,
  Paragraph,
  Subtitle,
  TextButton,
  Tooltip,
} from '@joor/design-system'
import styled from 'styled-components/macro'

import messages from '../../constants/messages'
import { AddProductsContext } from 'features/Orders/Review/components/AddProductsButton/AddProductsButton'
import AddProductsLink from 'features/Orders/Review/components/AddProductsButton/AddProductsLink'
import { OrderData } from 'features/Orders/Review/components/Overview/Overview'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding-block: 32px 52px;
  gap: 8px;

  p {
    margin: 0;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  .warning-icon svg g {
    fill: var(--color-error-600);
  }

  .refresh-button {
    color: var(--color-secondary-800);
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`

export const ProductsEmpty = ({
  orderDetails,
  isButtonDisabled,
  isButtonHidden,
  reasons,
}: {
  orderDetails: {
    collections?: OrderData['collections']['edges']
    status: OrderData['status']
  }
  isButtonDisabled: boolean
  isButtonHidden: boolean
  reasons: string[]
}) => {
  return (
    <Container data-testid={ProductsEmptyTestIds.Container}>
      {!isButtonHidden ? (
        <>
          <Icon
            data-testid={ProductsEmptyTestIds.ShopBagIcon}
            iconName="shopping-bag"
            plain
            iconSize="extraLarge"
          />
          <Subtitle type="1" typeface="montserrat">
            {messages.empty.title}
          </Subtitle>
          <Paragraph type="3" typeface="montserrat" textAlign="center">
            {messages.empty.messageStart}
            <br />
            <AddProductsInEmpty
              orderDetails={orderDetails}
              isButtonDisabled={isButtonDisabled}
              reasons={reasons}
            />{' '}
            {messages.empty.messageEnd}
          </Paragraph>
        </>
      ) : (
        <Wrapper>
          <Icon
            className="warning-icon"
            iconName="exclamation"
            iconSize="extraLarge"
            plain
          />
          <Subtitle type="1" typeface="montserrat">
            {messages.empty.title}
          </Subtitle>
          <ul style={{ color: 'var(--color-error-600)', margin: 0 }}>
            {reasons.map((reason) => (
              <li>
                <Paragraph
                  type="3"
                  typeface="montserrat"
                  color={'error-600'}
                  textAlign={'left'}
                >
                  {reason}
                </Paragraph>
              </li>
            ))}
          </ul>
        </Wrapper>
      )}
    </Container>
  )
}

const AddProductsInEmpty = ({
  orderDetails,
  isButtonDisabled,
  reasons,
}: {
  orderDetails: {
    collections?: OrderData['collections']['edges']
    status: OrderData['status']
  }
  isButtonDisabled: boolean
  reasons: string[]
}) => {
  return (
    <AddProductsLink order={orderDetails}>
      <TextButtonContainer
        isButtonDisabled={isButtonDisabled}
        reasons={reasons}
      />
    </AddProductsLink>
  )
}

const TextButtonContainer = ({
  isButtonDisabled,
  reasons,
}: {
  isButtonDisabled: boolean
  reasons: string[]
}) => {
  const { handleAddProducts } = useContext(AddProductsContext)

  return (
    <Tooltip
      content={
        isButtonDisabled ? (
          <div>
            {reasons.map((reason) => (
              <p>{reason}</p>
            ))}
          </div>
        ) : (
          ''
        )
      }
    >
      <TextButton
        onClick={() => handleAddProducts()}
        disabled={isButtonDisabled}
      >
        {messages.empty.addProducts}
      </TextButton>
    </Tooltip>
  )
}

export const ProductsEmptyTestIds = {
  Container: 'products-empty-container',
  ShopBagIcon: 'shopping-bag-icon',
} as const
